// Face-landmark detection (MediaPipe Tasks Vision). Detects a single face in a
// portrait and converts the mesh into normalized region ellipses (eyes, brows,
// nose, mouth, beard) that the particle sampler can use INSTEAD of its fixed
// ellipses — so a new photo is placed correctly without manual tweaking.
//
// The heavy library + model are loaded lazily from CDN on first use and cached.
// Any failure (offline, blocked CDN, no face) returns null → caller falls back
// to the built-in heuristic regions. Nothing here is on the critical path.

import type { FaceRegions, Ellipse } from './avatar.js';

type LM = { x: number; y: number; z: number };
// Minimal shape of the MediaPipe FaceLandmarker we use.
interface Landmarker { detect(img: HTMLImageElement): { faceLandmarks: LM[][] } }

let landmarkerPromise: Promise<Landmarker | null> | null = null;

const WASM_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

async function getLandmarker(): Promise<Landmarker | null> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const fileset = await vision.FilesetResolver.forVisionTasks(WASM_CDN);
        const lm = await vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL },
          runningMode: 'IMAGE',
          numFaces: 1,
        });
        return lm as unknown as Landmarker;
      } catch {
        return null;
      }
    })();
  }
  return landmarkerPromise;
}

// Build a padded ellipse (normalized 0..1) bounding a set of landmark indices.
function ell(pts: LM[], idxs: number[], padX = 1.4, padY = 1.5): Ellipse | null {
  let minX = 1, minY = 1, maxX = 0, maxY = 0, n = 0;
  for (const i of idxs) {
    const p = pts[i];
    if (!p) continue;
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
    n++;
  }
  if (n < 2) return null;
  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    rx: Math.max(0.02, ((maxX - minX) / 2) * padX),
    ry: Math.max(0.02, ((maxY - minY) / 2) * padY),
  };
}

// Canonical FaceMesh indices (image-left region = subject's right).
const IDX = {
  eyeL: [33, 133, 159, 145, 160, 144, 153],
  eyeR: [362, 263, 386, 374, 387, 373, 380],
  browL: [70, 63, 105, 66, 107, 46],
  browR: [336, 296, 334, 293, 300, 276],
  nose: [1, 2, 98, 327, 168, 4],
  mouth: [61, 291, 13, 14, 0, 17, 78, 308],
  beardL: [135, 170, 140, 171, 136],
  beardR: [364, 395, 369, 396, 365],
};

/**
 * Detect a single face and return normalized region ellipses, or null if no face
 * / library unavailable. Safe to call from anywhere; never throws.
 */
export async function detectFaceRegions(img: HTMLImageElement): Promise<FaceRegions | null> {
  try {
    const lm = await getLandmarker();
    if (!lm) return null;
    const res = lm.detect(img);
    const pts = res.faceLandmarks?.[0];
    if (!pts || pts.length < 400) return null;

    const eyeL = ell(pts, IDX.eyeL, 1.5, 1.8);
    const eyeR = ell(pts, IDX.eyeR, 1.5, 1.8);
    const mouth = ell(pts, IDX.mouth, 1.25, 1.6);
    const nose = ell(pts, IDX.nose, 1.3, 1.2);
    if (!eyeL || !eyeR || !mouth || !nose) return null; // core features required

    return {
      eyeL, eyeR, mouth, nose,
      browL: ell(pts, IDX.browL, 1.4, 2.0) ?? { x: eyeL.x, y: eyeL.y - 0.05, rx: eyeL.rx * 1.2, ry: 0.03 },
      browR: ell(pts, IDX.browR, 1.4, 2.0) ?? { x: eyeR.x, y: eyeR.y - 0.05, rx: eyeR.rx * 1.2, ry: 0.03 },
      beardL: ell(pts, IDX.beardL, 1.6, 1.6) ?? { x: 0.22, y: 0.82, rx: 0.12, ry: 0.12 },
      beardR: ell(pts, IDX.beardR, 1.6, 1.6) ?? { x: 0.78, y: 0.82, rx: 0.12, ry: 0.12 },
    };
  } catch {
    return null;
  }
}
